import { Theme } from "src/store/slices/client/client.type";

function applyOpacityToHexColor(color: string, opacityHex: string = "0a") {
  const isValidHex = /^#([A-Fa-f0-9]{3}){1,2}$/.test(color);
  if (!isValidHex) {
    return color;
  }
  if (color.length === 4) {
    color = `#${[...color.slice(1)].map((x) => x + x).join("")}`;
  }
  return `${color}${opacityHex}`;
}

function getCssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function getCssVarHex(name: string) {
  const value = getCssVar(name);
  return /^#([A-Fa-f0-9]{8})$/.test(value) ? value.slice(0, 7) : value;
}

function lightnessForStep(step: number) {
  const t = (step - 10) / (100 - 10);
  return 95 + t * (15 - 95);
}

function hexToHsl(hex: string) {
  hex = hex.replace("#", "");
  if (hex.length === 8) hex = hex.slice(0, 6);
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h = (h ?? 0) / 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number) {
  h = (((h % 360) + 360) % 360) / 360;
  s = Math.min(100, Math.max(0, s)) / 100;
  l = Math.min(100, Math.max(0, l)) / 100;

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function generateScale(inputHex: string, name = "color") {
  const STEPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const S_TABLE = {
    10: 95,
    20: 95,
    30: 95,
    40: 85,
    50: 75,
    60: 75,
    70: 85,
    80: 95,
    90: 95,
    100: 95,
  };
  const { h } = hexToHsl(inputHex);

  return STEPS.map((step: number) => {
    const l = lightnessForStep(step);
    const s = S_TABLE[step as keyof typeof S_TABLE];
    const hex = hslToHex(h, s, l);
    return {
      name: `--color-${name}-${step}`,
      hex,
      hsl: { h: +h.toFixed(1), s, l: +l.toFixed(1) },
    };
  });
}

function normalizeHexColor(color?: string | null) {
  let hex = (color ?? "").trim();
  if (!hex) return null;
  if (!hex.startsWith("#")) hex = `#${hex}`;
  if (hex.length === 4) {
    hex = `#${[...hex.slice(1)].map((x) => x + x).join("")}`;
  }
  if (hex.length === 9) {
    hex = hex.slice(0, 7);
  }
  return /^#[A-Fa-f0-9]{6}$/.test(hex) ? hex.toUpperCase() : null;
}

function generateThemeTokens(theme?: Partial<Theme> | null) {
  const THEME_ANCHOR_STEP = 60;
  const tokens: Record<string, string> = {};

  (
    [
      ["backend-primary", theme?.primaryColor],
      ["backend-secondary", theme?.secondaryColor],
    ] as const
  ).forEach(([name, rawColor]) => {
    const baseHex = normalizeHexColor(rawColor);
    if (!baseHex) return;
    generateScale(baseHex, name).forEach(({ name: token, hex }) => {
      tokens[token] = token === `--color-${name}-${THEME_ANCHOR_STEP}` ? baseHex : hex;
    });
  });

  return tokens;
}

function applyThemeTokens(theme?: Partial<Theme> | null) {
  const tokens = generateThemeTokens(theme);
  const root = typeof document !== "undefined" ? document.documentElement : null;
  if (root) {
    Object.entries(tokens).forEach(([token, value]) => root.style.setProperty(token, value));
  }
  return tokens;
}

export {
  applyOpacityToHexColor,
  getCssVar,
  getCssVarHex,
  generateScale,
  normalizeHexColor,
  generateThemeTokens,
  applyThemeTokens,
};
